import React from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { useProducts } from '../context/ProductsContext'
import { API_BASE } from '../config'
import { isRx } from '../utils/rx'
import ReferAndEarn from '../components/ReferAndEarn'

// Map a doctor's diagnosis → a shoppable concern (label + search term).
// Used only to surface RELEVANT products; we never claim a product was
// "handpicked" unless the doctor actually curated it.
const DIAG_CONCERN = [
  [/acne|pimple|breakout/i, 'Acne', 'acne'],
  [/pigment|melasma|dark ?spot|tan/i, 'Pigmentation', 'pigment'],
  [/hair|alopecia|bald|hairfall/i, 'Hair Care', 'hair'],
  [/eczema|dermatit|dry|sensitiv/i, 'Dry / Sensitive Skin', 'moisturi'],
  [/wrinkle|age|aging|ageing|fine ?line/i, 'Anti-Ageing', 'retinol'],
  [/dandruff|scalp|seborrh/i, 'Dandruff', 'ketoconazole'],
  [/psorias/i, 'Psoriasis', 'psoria'],
]
const deriveConcern = dx => {
  const d = String(dx || '')
  for (const [re, label, term] of DIAG_CONCERN) if (re.test(d)) return { label, term }
  return null
}

// Cloudinary transform: trim the wide white canvas around the product and pad
// to a clean white square, so small thumbnails show the product filling the
// frame instead of a tiny letterboxed sliver. q_auto/f_auto = faster loads.
const cld = (url, size = 240) => {
  if (!url || !url.includes('/upload/')) return url
  return url.replace('/upload/', `/upload/e_trim/c_pad,b_white,w_${size},h_${size},q_auto,f_auto/`)
}

// Revive green theme (Clinikally-style layout, our brand colors)
const T = {
  g900: '#0D2218', g800: '#1A3A28', g700: '#235233', g600: '#2D6B42', g500: '#3D8A57',
  g100: '#E8F5EE', g50: '#F5FAF7', mute: '#8a9a92', text: '#1A2820', border: '#EAF1ED', headerGrad: 'linear-gradient(150deg,#0F3D24 0%,#1E6B3C 60%,#2E8B4F 100%)',
  tintA: '#FDF3EE', tintB: '#EEF5FD', tintC: '#F5EEFD', tintD: '#FDFBEE', tintE: '#EEFDF3', tintF: '#FDEEF4',
}
const FONT = "'DM Sans',sans-serif"
const SERIF = "'DM Serif Display',serif"

// Pinch-to-zoom + drag-to-pan image. Capacitor's WebView disables native pinch,
// so we handle touches ourselves. touchAction:'none' hands gestures to our JS.
function ZoomableImage({ src, alt, onNext, onPrev }) {
  const [t, setT] = React.useState({ scale: 1, x: 0, y: 0 })
  const g = React.useRef({ mode: null, startDist: 0, startScale: 1, startX: 0, startY: 0, lastTap: 0, swX: 0, swY: 0 })
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
  const dist = ts => Math.hypot(ts[0].clientX - ts[1].clientX, ts[0].clientY - ts[1].clientY)
  const mid = ts => ({ x: (ts[0].clientX + ts[1].clientX) / 2, y: (ts[0].clientY + ts[1].clientY) / 2 })

  const onStart = e => {
    const s = g.current
    if (e.touches.length === 2) {
      s.mode = 'pinch'; s.startDist = dist(e.touches) || 1; s.startScale = t.scale
    } else if (e.touches.length === 1) {
      const now = Date.now()
      if (now - s.lastTap < 300) { // double-tap toggles zoom
        setT(p => p.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.6, x: 0, y: 0 })
        s.lastTap = 0; s.mode = null; return
      }
      s.lastTap = now
      s.mode = 'pan'; s.startX = e.touches[0].clientX - t.x; s.startY = e.touches[0].clientY - t.y
      s.swX = e.touches[0].clientX; s.swY = e.touches[0].clientY
    }
  }
  const onMove = e => {
    const s = g.current
    if (s.mode === 'pinch' && e.touches.length === 2) {
      const scale = clamp(s.startScale * (dist(e.touches) / s.startDist), 1, 5)
      setT(p => ({ ...p, scale }))
    } else if (s.mode === 'pan' && e.touches.length === 1 && t.scale > 1) {
      setT(p => ({ ...p, x: e.touches[0].clientX - s.startX, y: e.touches[0].clientY - s.startY }))
    }
  }
  const onEnd = e => {
    const s = g.current
    if (e.touches.length === 0) {
      // Not zoomed → a horizontal swipe changes the photo
      if (t.scale <= 1.03 && e.changedTouches && e.changedTouches[0]) {
        const dx = e.changedTouches[0].clientX - s.swX
        const dy = e.changedTouches[0].clientY - s.swY
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.3) { dx < 0 ? (onNext && onNext()) : (onPrev && onPrev()) }
      }
      s.mode = null; setT(p => p.scale <= 1.03 ? { scale: 1, x: 0, y: 0 } : p)
    }
    else if (e.touches.length === 1) { s.mode = 'pan'; s.startX = e.touches[0].clientX - t.x; s.startY = e.touches[0].clientY - t.y }
  }

  return (
    <div onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd}
      style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', touchAction: 'none' }}>
      <img src={src} alt={alt} draggable={false}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', transform: `translate(${t.x}px,${t.y}px) scale(${t.scale})`, transition: g.current.mode ? 'none' : 'transform .15s ease', willChange: 'transform', userSelect: 'none', WebkitUserSelect: 'none' }} />
    </div>
  )
}

const CONCERNS = [
  ['Acne', 'Treat breakouts with salicylic acid', 'acne', T.tintA],
  ['Pigmentation', 'Reduce dark spots with kojic acid', 'pigment', T.tintB],
  ['Dullness', 'Boost radiance with vitamin C', 'vitamin c', T.tintC],
  ['Anti-Aging', 'Smooth fine lines with retinol', 'retinol', T.tintD],
  ['Hairfall', 'Strengthen roots with biotin', 'hair', T.tintE],
  ['Dandruff', 'Control flakes with ketoconazole', 'ketoconazole', T.tintF],
]
const CATEGORIES = ['Sunscreen', 'Moisturizers', 'Face Wash', 'Serums', 'Toners', 'De-Pigmenting', 'Hair Care', 'Skincare']

// ───────────────────────── shared bits ─────────────────────────

function TopBar({ cartCount, onCartOpen }) {
  const navigate = useNavigate()
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3rem 1.1rem .8rem', background: '#fff' }}>
      <div onClick={() => navigate('/')} style={{ fontFamily: SERIF, fontSize: '1.45rem', color: T.g700, fontWeight: 700 }}>Revive</div>
      <button onClick={onCartOpen ? onCartOpen : () => navigate('/view-cart')} style={{ position: 'relative', background: 'none', border: 'none', fontSize: '1.35rem', cursor: 'pointer' }}>
        🛒
        {cartCount > 0 && <span style={{ position: 'absolute', top: -6, right: -8, background: T.g600, color: '#fff', borderRadius: 999, fontSize: '.58rem', fontWeight: 700, padding: '1px 5px' }}>{cartCount}</span>}
      </button>
    </div>
  )
}

function SearchBar() {
  const navigate = useNavigate()
  const { products } = useProducts()
  const [q, setQ] = React.useState('')
  const hits = React.useMemo(() => {
    const s = q.trim().toLowerCase()
    if (s.length < 2) return []
    return products.filter(p => (p.name + ' ' + (p.brand || '')).toLowerCase().includes(s)).slice(0, 8)
  }, [q, products])
  return (
    <div style={{ position: 'relative', padding: '0 1.1rem .6rem', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1.5px solid ${T.border}`, borderRadius: 14, padding: '.7rem .9rem', background: '#fff' }}>
        <span style={{ color: T.mute }}>🔍</span>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={'Search "sunscreen"'}
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: '.95rem', fontFamily: FONT, color: T.text, background: 'transparent' }} />
        {q && <span onClick={() => setQ('')} style={{ color: T.mute, cursor: 'pointer' }}>×</span>}
      </div>
      {hits.length > 0 && (
        <div style={{ position: 'absolute', left: '1.1rem', right: '1.1rem', top: '100%', zIndex: 50, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: '0 12px 30px rgba(13,34,24,.12)', overflow: 'hidden' }}>
          {hits.map(p => (
            <div key={p.id} onClick={() => { setQ(''); navigate(`/product/${p.id}`) }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '.6rem .8rem', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
              {(p.imgs && p.imgs[0]) && <img src={cld(p.imgs[0], 80)} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 6, background: '#fff' }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.82rem', color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize: '.66rem', color: T.mute }}>₹{p.price}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ProductCard({ p, onAdd, w = 168 }) {
  const navigate = useNavigate()
  return (
    <div style={{ width: w, flexShrink: 0, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
      <div onClick={() => navigate(`/product/${p.id}`)} style={{ position: 'relative', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', cursor: 'pointer' }}>
        {p.badge && <span style={{ position: 'absolute', top: 8, left: 8, background: T.g100, color: T.g700, fontSize: '.58rem', fontWeight: 700, padding: '.2rem .5rem', borderRadius: 999 }}>{p.badge}</span>}
        {(p.imgs && p.imgs[0]) ? <img src={cld(p.imgs[0], 340)} alt={p.name} style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }} /> : <span style={{ fontSize: '2rem', opacity: .2 }}>🧴</span>}
      </div>
      <div style={{ padding: '.6rem .75rem .8rem' }}>
        <div onClick={() => navigate(`/product/${p.id}`)} style={{ fontSize: '.8rem', color: T.text, lineHeight: 1.35, height: 34, overflow: 'hidden', cursor: 'pointer' }}>{p.name}</div>
        {isRx(p) && <div style={{ display: 'inline-block', fontSize: '.54rem', fontWeight: 700, color: '#B7770D', background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 6, padding: '.1rem .4rem', marginTop: 4 }}>℞ Prescription</div>}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '.5rem' }}>
          <div style={{ fontWeight: 700, color: T.g900, fontSize: '.92rem' }}>₹{p.price}</div>
          <button onClick={() => onAdd && onAdd(p)} style={{ background: T.g700, color: '#fff', border: 'none', borderRadius: 999, padding: '.4rem 1rem', fontWeight: 700, fontSize: '.78rem', cursor: 'pointer', fontFamily: FONT }}>ADD</button>
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ children, sub }) {
  return (
    <div style={{ padding: '1.4rem 1.1rem .8rem' }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: T.text, fontFamily: FONT }}>{children}</div>
      {sub && <div style={{ fontSize: '.8rem', color: T.mute, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// ───────────────────────── HOME ─────────────────────────

export function MobileShopHome({ onAdd, cartCount, onCartOpen }) {
  const navigate = useNavigate()
  const { products } = useProducts()
  const { user, fetchWithAuth } = useAuth()

  // Latest doctor diagnosis → honest, concern-matched recommendations.
  const [dx, setDx] = React.useState(null)
  React.useEffect(() => {
    let alive = true
    if (!user?.email) { setDx(null); return }
    ;(async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE}/api/followups?email=${encodeURIComponent(user.email)}`)
        if (!res.ok) return
        const j = await res.json()
        const withDx = (j.followups || []).filter(a => a.diagnosis)
        if (alive && withDx.length) setDx(withDx.sort((a, b) => new Date(b.date) - new Date(a.date))[0].diagnosis)
      } catch {}
    })()
    return () => { alive = false }
  }, [user, fetchWithAuth])

  const concern = dx ? deriveConcern(dx) : null
  const recProducts = React.useMemo(() => {
    if (!concern) return []
    const seen = new Set(), out = []
    for (const p of products) {
      if (p.inStock === 0 || p.needsImage === 1 || !(p.imgs && p.imgs[0] && p.price)) continue
      if (!((p.name || '') + ' ' + (p.tags || []).join(' ') + ' ' + (p.cat || '')).toLowerCase().includes(concern.term)) continue
      if (seen.has(p.imgs[0])) continue          // avoid duplicate images (e.g. 5/10/20mg variants)
      seen.add(p.imgs[0]); out.push(p)
      if (out.length >= 6) break
    }
    return out
  }, [products, concern])
  const personalised = !!(concern && recProducts.length)

  const catThumb = React.useMemo(() => {
    const m = {}
    for (const p of products) {
      if (!m[p.cat] && p.imgs && p.imgs[0]) m[p.cat] = p.imgs[0]
    }
    return m
  }, [products])

  const trending = React.useMemo(() => products.filter(p => p.imgs && p.imgs[0] && p.price).slice(12, 24), [products])

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT, paddingBottom: 92 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '3rem 1.1rem .5rem' }}>
        <div>
          <div style={{ fontFamily: SERIF, fontSize: '2rem', color: T.text, lineHeight: 1 }}>Shop</div>
          <div style={{ fontSize: '.82rem', color: T.mute, marginTop: 6, maxWidth: 230, lineHeight: 1.4 }}>Doctor-approved skincare, curated for you 🌿</div>
        </div>
        <button onClick={onCartOpen ? onCartOpen : () => navigate('/view-cart')} style={{ position: 'relative', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', marginTop: 4 }}>
          🛒
          {cartCount > 0 && <span style={{ position: 'absolute', top: -4, right: -6, background: T.g600, color: '#fff', borderRadius: 999, fontSize: '.6rem', fontWeight: 700, padding: '1px 5px' }}>{cartCount}</span>}
        </button>
      </div>

      <SearchBar />

      {/* Recommended-for-you hero — personalised only when the doctor has given a diagnosis */}
      <div style={{ padding: '.4rem 1.1rem 0' }}>
        {personalised ? (
          <div onClick={() => navigate(`/shop?search=${encodeURIComponent(concern.term)}`)}
            style={{ background: `linear-gradient(135deg,${T.g100},#F4FBF6)`, border: `1px solid ${T.border}`, borderRadius: 20, padding: '1.2rem', cursor: 'pointer', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 999, padding: '.25rem .7rem', fontSize: '.62rem', fontWeight: 700, color: T.g700 }}>⭐ Recommended for you</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, padding: '.2rem .5rem', fontSize: '.56rem', fontWeight: 700, color: T.g700, textAlign: 'right' }}>🛡️ Dermatologist<br />Recommended</div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: '.8rem', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: SERIF, fontSize: '1.35rem', color: T.text, lineHeight: 1.15 }}>For your <span style={{ color: T.g700 }}>{concern.label}</span></div>
                <div style={{ fontSize: '.76rem', color: '#5a6a62', marginTop: 6, lineHeight: 1.45 }}>Dermatologist-recommended products matched to your skin concern.</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: T.g700, color: '#fff', borderRadius: 999, padding: '.55rem 1.1rem', fontWeight: 700, fontSize: '.82rem' }}>View recommendations →</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                {recProducts.slice(0, 3).map(p => (
                  <div key={p.id} style={{ width: 54, height: 54, borderRadius: 12, background: '#fff', border: `1px solid ${T.border}`, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,.06)' }}>
                    <img src={cld(p.imgs[0], 140)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div onClick={() => navigate('/m/consult')}
            style={{ background: `linear-gradient(135deg,${T.g100},#F4FBF6)`, border: `1px solid ${T.border}`, borderRadius: 20, padding: '1.3rem', cursor: 'pointer' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 999, padding: '.25rem .7rem', fontSize: '.62rem', fontWeight: 700, color: T.g700 }}>🩺 Personalised for your skin</div>
            <div style={{ fontFamily: SERIF, fontSize: '1.3rem', color: T.text, marginTop: '.7rem', lineHeight: 1.2 }}>Get products matched to <span style={{ color: T.g700 }}>your skin</span></div>
            <div style={{ fontSize: '.78rem', color: '#5a6a62', marginTop: 6, lineHeight: 1.45, maxWidth: 260 }}>Consult a dermatologist and we'll recommend a routine based on your diagnosis — no guesswork.</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, background: T.g700, color: '#fff', borderRadius: 999, padding: '.55rem 1.2rem', fontWeight: 700, fontSize: '.82rem' }}>Consult a dermatologist →</div>
          </div>
        )}
      </div>

      {/* Shop by Category / Derma Consult tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '1rem 1.1rem 0' }}>
        <button onClick={() => navigate('/m/categories')} style={{ background: T.g100, border: `1px solid ${T.border}`, borderRadius: 16, padding: '1rem', textAlign: 'left', cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: '1.3rem' }}>🛍️</div>
            <div style={{ fontWeight: 800, color: T.g900, fontSize: '.92rem', marginTop: 6 }}>Shop by Category</div>
            <div style={{ fontSize: '.66rem', color: T.mute, marginTop: 2 }}>Explore all products</div>
          </div>
          <span style={{ width: 26, height: 26, borderRadius: '50%', background: T.g600, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem', flexShrink: 0 }}>→</span>
        </button>
        <button onClick={() => navigate('/m/consult')} style={{ background: '#FDF3EE', border: '1px solid #F1D9CC', borderRadius: 16, padding: '1rem', textAlign: 'left', cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: '1.3rem' }}>👩‍⚕️</div>
            <div style={{ fontWeight: 800, color: T.g900, fontSize: '.92rem', marginTop: 6 }}>Derma Consult</div>
            <div style={{ fontSize: '.66rem', color: T.mute, marginTop: 2 }}>Talk to a dermatologist</div>
          </div>
          <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#E8956B', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem', flexShrink: 0 }}>→</span>
        </button>
      </div>

      {/* Shop by Category */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem 1.1rem .8rem' }}>
        <div style={{ fontSize: '1.15rem', fontWeight: 800, color: T.text }}>Shop by Category</div>
        <span onClick={() => navigate('/m/categories')} style={{ fontSize: '.78rem', color: T.g700, fontWeight: 700, cursor: 'pointer' }}>View all ›</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, padding: '0 1.1rem' }}>
        {CATEGORIES.map(c => (
          <div key={c} onClick={() => navigate(`/shop?cat=${encodeURIComponent(c)}`)} style={{ cursor: 'pointer', textAlign: 'center' }}>
            <div style={{ position: 'relative', width: '100%', paddingBottom: '100%', borderRadius: '50%', background: T.g50, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {catThumb[c] ? <img src={cld(catThumb[c], 160)} alt={c} style={{ maxWidth: '72%', maxHeight: '72%', objectFit: 'contain' }} /> : <span style={{ opacity: .25, fontSize: '1.2rem' }}>🧴</span>}
              </div>
            </div>
            <div style={{ fontSize: '.6rem', color: T.text, marginTop: 5, fontWeight: 600, lineHeight: 1.2 }}>{c}</div>
          </div>
        ))}
      </div>

      {/* Shop by Concern */}
      <div style={{ padding: '1.5rem 1.1rem .8rem' }}>
        <div style={{ fontSize: '1.15rem', fontWeight: 800, color: T.text }}>Shop by Concern</div>
        <div style={{ fontSize: '.76rem', color: T.mute, marginTop: 2 }}>Find solutions for your specific skin needs</div>
      </div>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '0 1.1rem .4rem', WebkitOverflowScrolling: 'touch' }}>
        {CONCERNS.map(([name, desc, q, tint]) => (
          <div key={name} onClick={() => navigate(`/shop?search=${encodeURIComponent(q)}`)}
            style={{ flexShrink: 0, width: 210, background: tint, borderRadius: 16, padding: '1rem', cursor: 'pointer' }}>
            <div style={{ fontFamily: SERIF, fontSize: '1.05rem', color: T.text }}>{name}</div>
            <div style={{ fontSize: '.72rem', color: '#5a6a62', marginTop: 4, lineHeight: 1.4, height: 32, overflow: 'hidden' }}>{desc}</div>
            <div style={{ marginTop: 8, color: T.g700, fontWeight: 700 }}>→</div>
          </div>
        ))}
      </div>

      {/* Dermatologist-recommended — no fake "bestseller"/ratings; we have no sales or review data */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem 1.1rem .8rem' }}>
        <div>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: T.text }}>Dermatologist-Recommended</div>
          <div style={{ fontSize: '.76rem', color: T.mute, marginTop: 2 }}>Science-backed skincare essentials</div>
        </div>
        <span onClick={() => navigate('/shop')} style={{ fontSize: '.78rem', color: T.g700, fontWeight: 700, cursor: 'pointer' }}>View all ›</span>
      </div>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '0 1.1rem .4rem', WebkitOverflowScrolling: 'touch' }}>
        {trending.map(p => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}
      </div>

      {/* Consult banner */}
      <div onClick={() => navigate('/m/consult')} style={{ margin: '1.4rem 1.1rem 0', background: `linear-gradient(135deg,${T.g100},#fff)`, border: `1px solid ${T.g500}`, borderRadius: 18, padding: '1.2rem', cursor: 'pointer' }}>
        <div style={{ fontSize: '.62rem', letterSpacing: '.1em', color: T.g600, textTransform: 'uppercase', fontWeight: 700 }}>Derma Consult</div>
        <div style={{ fontFamily: SERIF, fontSize: '1.25rem', color: T.g900, margin: '.35rem 0 .7rem' }}>Get expert advice from a dermatologist</div>
        <span style={{ background: T.g700, color: '#fff', borderRadius: 999, padding: '.55rem 1.2rem', fontWeight: 700, fontSize: '.85rem' }}>Consult now at ₹500</span>
      </div>
    </div>
  )
}

// ───────────────────────── SHOP (app-style product list) ─────────────────────────

export function MobileShopList({ onAdd, cartCount, onCartOpen }) {
  const { products, loading } = useProducts()
  const { search: qs } = useLocation()
  const params = new URLSearchParams(qs)
  const [cat, setCat] = React.useState(params.get('cat') || 'All')
  const [q, setQ] = React.useState(params.get('search') || '')
  const [limit, setLimit] = React.useState(30)

  const items = React.useMemo(() => {
    let list = products
    if (cat !== 'All') list = list.filter(p => p.cat === cat)
    const s = q.trim().toLowerCase()
    if (s) list = list.filter(p => (p.name + ' ' + (p.brand || '') + ' ' + (p.tags || []).join(' ')).toLowerCase().includes(s))
    return list
  }, [products, cat, q])

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT, paddingBottom: 92 }}>
      <TopBar cartCount={cartCount} onCartOpen={onCartOpen} />
      {/* search */}
      <div style={{ padding: '0 1.1rem .6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1.5px solid ${T.border}`, borderRadius: 14, padding: '.65rem .9rem' }}>
          <span style={{ color: T.mute }}>🔍</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '.95rem', fontFamily: FONT, color: T.text }} />
          {q && <span onClick={() => setQ('')} style={{ color: T.mute, cursor: 'pointer' }}>×</span>}
        </div>
      </div>
      {/* category chips */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '0 1.1rem .8rem', WebkitOverflowScrolling: 'touch' }}>
        {['All', ...CATEGORIES].map(c => (
          <button key={c} onClick={() => { setCat(c); setLimit(30) }}
            style={{ flexShrink: 0, background: cat === c ? T.g700 : '#fff', color: cat === c ? '#fff' : T.text, border: `1.5px solid ${cat === c ? T.g700 : T.border}`, borderRadius: 999, padding: '.45rem 1rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>{c}</button>
        ))}
      </div>
      <div style={{ padding: '0 1.1rem .5rem', fontSize: '.72rem', color: T.mute }}>{loading ? 'Loading…' : `${items.length} products`}</div>
      {/* grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '0 1.1rem' }}>
        {items.slice(0, limit).map(p => <ProductCard key={p.id} p={p} onAdd={onAdd} w="auto" />)}
      </div>
      {items.length > limit && (
        <div style={{ padding: '1rem 1.1rem' }}>
          <button onClick={() => setLimit(l => l + 30)} style={{ width: '100%', background: '#fff', color: T.g700, border: `1.5px solid ${T.g600}`, borderRadius: 12, padding: '.8rem', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: FONT }}>Load more</button>
        </div>
      )}
    </div>
  )
}

// ───────────────────────── CATEGORIES ─────────────────────────

export function MobileCategories({ onAdd }) {
  const navigate = useNavigate()
  const { products } = useProducts()
  const [active, setActive] = React.useState(CATEGORIES[0])
  const items = React.useMemo(() => products.filter(p => p.cat === active && p.imgs && p.imgs[0]).slice(0, 30), [products, active])
  const railThumb = React.useMemo(() => {
    const m = {}
    for (const p of products) if (!m[p.cat] && p.imgs && p.imgs[0]) m[p.cat] = p.imgs[0]
    return m
  }, [products])

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT, paddingBottom: 92 }}>
      <TopBar />
      <div style={{ padding: '0 1.1rem .6rem', fontSize: '1.25rem', fontWeight: 800, color: T.text }}>Shop by Category</div>
      {/* horizontal category strip (top) */}
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', padding: '.7rem 1.1rem 1rem', borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, WebkitOverflowScrolling: 'touch' }}>
        {CATEGORIES.map(c => (
          <div key={c} onClick={() => setActive(c)} style={{ flexShrink: 0, width: 68, textAlign: 'center', cursor: 'pointer' }}>
            <div style={{ width: 60, height: 60, margin: '0 auto', borderRadius: '50%', background: T.g50, border: `2px solid ${active === c ? T.g500 : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {railThumb[c] ? <img src={cld(railThumb[c], 120)} alt="" style={{ maxWidth: '78%', maxHeight: '78%', objectFit: 'contain' }} /> : '🧴'}
            </div>
            <div style={{ fontSize: '.64rem', color: active === c ? T.g800 : T.mute, fontWeight: active === c ? 700 : 500, marginTop: 5, lineHeight: 1.2 }}>{c}</div>
          </div>
        ))}
      </div>
      {/* product grid (full width) */}
      <div style={{ padding: '1rem 1.1rem 0' }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: T.g700, marginBottom: '.75rem' }}>{active}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {items.map(p => (
            <div key={p.id} onClick={() => navigate(`/product/${p.id}`)} style={{ cursor: 'pointer' }}>
              <div style={{ height: 120, borderRadius: 12, background: T.g50, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={cld(p.imgs[0], 200)} alt="" style={{ maxWidth: '88%', maxHeight: '88%', objectFit: 'contain' }} />
              </div>
              <div style={{ fontSize: '.72rem', color: T.text, marginTop: 5, height: 30, overflow: 'hidden', lineHeight: 1.3 }}>{p.name}</div>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: T.g900 }}>₹{p.price}</div>
            </div>
          ))}
        </div>
        <button onClick={() => navigate(`/shop?cat=${encodeURIComponent(active)}`)} style={{ width: '100%', margin: '1rem 0', background: '#fff', color: T.g700, border: `1.5px solid ${T.g600}`, borderRadius: 10, padding: '.7rem', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', fontFamily: FONT }}>View all {active} →</button>
      </div>
    </div>
  )
}

// ───────────────────────── CONSULT ─────────────────────────

export function MobileConsult() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const loggedIn = !!user?.token
  // Real dermatologist team — shows the doctor while there's one, generalises as more are approved
  const [docs, setDocs] = React.useState([])
  React.useEffect(() => {
    fetch(`${API_BASE}/api/doctors`).then(r => r.ok ? r.json() : { doctors: [] })
      .then(j => setDocs(Array.isArray(j?.doctors) ? j.doctors : [])).catch(() => {})
  }, [])
  const docLine = docs.length === 1
    ? `${docs[0].name}${docs[0].qualification ? ` · ${docs[0].qualification}` : ''}`
    : 'Verified MD-qualified dermatologists'
  const concerns = [['🧑', 'Acne & Scars'], ['💇', 'Hair Loss'], ['🌗', 'Pigmentation'], ['🌿', 'Eczema'], ['🩹', 'Psoriasis'], ['✨', 'Anti-aging']]
  const features = [['📹', 'Video\nConsultation'], ['📄', 'Digital\nPrescription'], ['🔔', 'Daily\nReminders'], ['📈', 'Progress\nTracking'], ['🔁', 'Continuous\nFollow-up']]
  const steps = [
    ['👩‍⚕️', 'Consult', 'Talk to our expert dermatologist online'],
    ['📋', 'Get Treatment Plan', 'Receive your digital prescription & skincare plan'],
    ['📱', 'Follow & Track', 'Get daily reminders and track your progress'],
    ['📈', 'Better Skin', 'We support you until you achieve your best skin'],
  ]
  const trust = [
    ['🩺', 'Doctor-led', 'Every plan by an MD'],
    ['🛡️', 'Verified', 'Licensed dermatologists'],
    ['🔒', 'Secure', 'Private & encrypted'],
    ['💬', 'Continuous', 'Support after your consult'],
  ]
  return (
    <div style={{ minHeight: '100vh', background: T.g50, fontFamily: FONT, paddingBottom: 92 }}>
      {/* Hero */}
      <div style={{ background: T.headerGrad, padding: '3.2rem 1.3rem 1.6rem', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: SERIF, fontSize: '1.65rem', lineHeight: 1.2 }}>Consult Once.</div>
            <div style={{ fontFamily: SERIF, fontSize: '1.65rem', lineHeight: 1.2, color: T.g300 }}>We'll guide you until you're better.</div>
          </div>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div style={{ position: 'relative', width: 84, height: 84, borderRadius: '50%', background: 'rgba(255,255,255,.14)', border: '2px solid rgba(255,255,255,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.4rem' }}>
              👩‍⚕️
              <span style={{ position: 'absolute', bottom: -2, right: -2, width: 22, height: 22, borderRadius: '50%', background: T.g500, border: '2px solid #fff', color: '#fff', fontSize: '.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>
            </div>
          </div>
        </div>
        <div style={{ fontSize: '.85rem', color: 'rgba(255,255,255,.75)', marginTop: 12, lineHeight: 1.5 }}>Expert care. Continuous support.<br />Better skin, every day.</div>
        <div style={{ marginTop: 12, fontSize: '.82rem', fontWeight: 700 }}>{docLine}</div>
      </div>

      {/* Feature icons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4, padding: '1.1rem .9rem', background: '#fff', borderBottom: `1px solid ${T.border}` }}>
        {features.map(([ic, label]) => (
          <div key={label} style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '1.25rem' }}>{ic}</div>
            <div style={{ fontSize: '.6rem', color: T.text, fontWeight: 600, marginTop: 5, lineHeight: 1.3, whiteSpace: 'pre-line' }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '1.1rem' }}>
        {/* CTA banner */}
        <button onClick={() => navigate('/book-appointment')} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: `linear-gradient(135deg,${T.g800},${T.g700})`, border: 'none', borderRadius: 16, padding: '1.1rem 1.3rem', cursor: 'pointer', fontFamily: FONT, textAlign: 'left', boxShadow: '0 6px 20px rgba(35,82,51,.25)' }}>
          <span style={{ fontSize: '1.5rem' }}>📹</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>Consult Now at ₹500</div>
            <div style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.65)', marginTop: 2 }}>Secure · Private · Doctor-led care</div>
          </div>
          <span style={{ color: 'rgba(255,255,255,.7)', fontSize: '1.2rem' }}>›</span>
        </button>

        {/* Returning-patient path */}
        {loggedIn && (
          <button onClick={() => navigate('/patient-dashboard?view=care')} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: '.85rem 1.1rem', marginTop: 10, cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
            <span style={{ fontSize: '1.1rem' }}>📋</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '.85rem', fontWeight: 700, color: T.text }}>Already a patient?</div>
              <div style={{ fontSize: '.72rem', color: T.mute, marginTop: 1 }}>Open My Care to see your plan & follow-ups</div>
            </div>
            <span style={{ color: T.g600 }}>›</span>
          </button>
        )}

        {/* How Revive Works */}
        <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 18, padding: '1.2rem', marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: T.text }}>How Revive Works</div>
            <div style={{ fontSize: '.68rem', color: T.mute }}>Your journey to healthy skin</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem' }}>
            {steps.map(([ic, title, sub], i) => (
              <div key={title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0, position: 'relative' }}>
                  {ic}
                  <span style={{ position: 'absolute', top: -4, left: -4, width: 18, height: 18, borderRadius: '50%', background: T.g700, color: '#fff', fontSize: '.6rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '.88rem', fontWeight: 700, color: T.text }}>{title}</div>
                  <div style={{ fontSize: '.74rem', color: T.mute, marginTop: 2, lineHeight: 1.45 }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common concerns */}
        <div style={{ fontWeight: 800, fontSize: '1rem', color: T.text, margin: '1.4rem 0 .8rem' }}>Consult for common concerns</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {concerns.map(([ic, c]) => (
            <div key={c} onClick={() => navigate(`/book-appointment?concern=${encodeURIComponent(c)}`)} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, padding: '.85rem 1rem', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <span style={{ fontSize: '1.1rem' }}>{ic}</span>
              <span style={{ flex: 1, fontSize: '.85rem', color: T.text, fontWeight: 600 }}>{c}</span>
              <span style={{ color: T.g600 }}>›</span>
            </div>
          ))}
        </div>

        {/* AI Skin Analysis — honest framing: a first look your doctor reviews */}
        <div style={{ marginTop: 16, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '1.1rem', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: '1.8rem' }}>🔬</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '.72rem', color: T.mute }}>Not sure if you need a consultation?</div>
            <div style={{ fontSize: '.92rem', fontWeight: 800, color: T.text, margin: '2px 0' }}>Scan your skin first</div>
            <div style={{ fontSize: '.72rem', color: T.mute, lineHeight: 1.4 }}>Get a quick first look — a dermatologist reviews it.</div>
          </div>
          <button onClick={() => navigate('/skin-quiz')} style={{ background: T.g100, color: T.g700, border: 'none', borderRadius: 10, padding: '.5rem .8rem', fontWeight: 700, fontSize: '.72rem', cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap' }}>Try it ›</button>
        </div>

        {/* Trust bar — honest signals, no invented counts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '1.1rem' }}>
          {trust.map(([ic, title, sub]) => (
            <div key={title} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
              <span style={{ fontSize: '1.1rem' }}>{ic}</span>
              <div>
                <div style={{ fontSize: '.82rem', fontWeight: 800, color: T.text }}>{title}</div>
                <div style={{ fontSize: '.66rem', color: T.mute, marginTop: 1, lineHeight: 1.3 }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ───────────────────────── MY CONSULTS ─────────────────────────

const CONSULT_STATUS = {
  completed: ['Completed', T.g700, T.g100],
  confirmed: ['Confirmed', '#3451B2', '#EEF2FF'],
  pending:   ['Pending', '#B7770D', '#FFF8E1'],
  cancelled: ['Cancelled', '#C0392B', '#FFF0F0'],
}

export function MobileConsults() {
  const navigate = useNavigate()
  const { user, fetchWithAuth } = useAuth()
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [open, setOpen] = React.useState(null)

  React.useEffect(() => {
    if (!user?.token) { setLoading(false); return }
    let alive = true
    fetchWithAuth(`${API_BASE}/api/followups?email=${encodeURIComponent(user.email)}`).then(r => r.ok ? r.json() : { followups: [] })
      .then(j => { if (alive) { setItems((j.followups || []).slice().sort((a, b) => new Date(b.date) - new Date(a.date))); setLoading(false) } })
      .catch(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [user, fetchWithAuth])

  return (
    <div style={{ minHeight: '100vh', background: T.g50, fontFamily: FONT, paddingBottom: 92 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2.8rem 1.1rem 1rem', background: '#fff', borderBottom: `1px solid ${T.border}` }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: T.g900, cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
        <div style={{ fontFamily: SERIF, fontSize: '1.35rem', color: T.g900 }}>My Consults</div>
      </div>

      <div style={{ padding: '1.1rem' }}>
        {loading && <div style={{ textAlign: 'center', color: T.mute, padding: '2rem' }}>Loading…</div>}
        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', color: T.mute, padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.4rem', opacity: .2, marginBottom: 10 }}>🩺</div>
            <div style={{ fontWeight: 600, color: T.text }}>No consultations yet</div>
            <button onClick={() => navigate('/m/consult')} style={{ marginTop: 16, background: T.g700, color: '#fff', border: 'none', borderRadius: 999, padding: '.7rem 1.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Book a consult</button>
          </div>
        )}
        {items.map((a, i) => {
          const [sL, sC, sB] = CONSULT_STATUS[a.status] || CONSULT_STATUS.pending
          const id = a.id || i
          const isOpen = open === id
          const rx = (a.medication || a.prescription || '').trim()
          return (
            <div key={id} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 12, overflow: 'hidden' }}>
              <div onClick={() => setOpen(isOpen ? null : id)} style={{ padding: '1rem 1.1rem', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: '.72rem', color: T.mute }}>{(a.date || '').slice(0, 10)}{a.timeSlot ? ` · ${a.timeSlot}` : ''}</div>
                  <span style={{ fontSize: '.64rem', fontWeight: 800, color: sC, background: sB, borderRadius: 999, padding: '.2rem .7rem' }}>{sL}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                  <div style={{ fontWeight: 800, color: T.text, fontSize: '1rem' }}>{a.service || 'Consultation'}</div>
                  <span style={{ color: T.mute, fontSize: '1.1rem', transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
                </div>
                <div style={{ fontSize: '.75rem', color: T.mute, marginTop: 2 }}>🧑‍⚕️ {a.doctor || 'Your dermatologist'}</div>
              </div>
              {isOpen && (
                <div style={{ padding: '0 1.1rem 1.1rem', borderTop: `1px solid ${T.border}` }}>
                  {a.diagnosis && <div style={{ marginTop: 12 }}><div style={{ fontSize: '.66rem', fontWeight: 800, color: T.mute, textTransform: 'uppercase', letterSpacing: '.04em' }}>Diagnosis</div><div style={{ fontSize: '.85rem', color: T.text, marginTop: 3 }}>{a.diagnosis}</div></div>}
                  {rx && <div style={{ marginTop: 12 }}><div style={{ fontSize: '.66rem', fontWeight: 800, color: T.mute, textTransform: 'uppercase', letterSpacing: '.04em' }}>Prescription</div><div style={{ fontSize: '.85rem', color: T.text, marginTop: 3, whiteSpace: 'pre-wrap', lineHeight: 1.5, background: T.g50, borderRadius: 8, padding: '.6rem .7rem' }}>{rx}</div></div>}
                  {a.followUpDate && <div style={{ marginTop: 12, fontSize: '.8rem', color: T.text }}>📅 <b>Follow-up:</b> {String(a.followUpDate).slice(0, 10)}</div>}
                  {a.mode && <div style={{ marginTop: 6, fontSize: '.78rem', color: T.mute }}>💻 {a.mode}</div>}
                  {!a.diagnosis && !rx && !a.followUpDate && <div style={{ marginTop: 12, fontSize: '.8rem', color: T.mute }}>No prescription or notes recorded for this consultation yet.</div>}
                  <button onClick={(e) => { e.stopPropagation(); navigate('/patient-dashboard?view=care') }} style={{ marginTop: 14, background: 'none', border: 'none', color: T.g700, fontWeight: 700, fontSize: '.8rem', cursor: 'pointer', fontFamily: FONT, padding: 0 }}>Open in My Care →</button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ───────────────────────── PROFILE ─────────────────────────

function ModalShell({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 960, background: 'rgba(13,34,24,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 520, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: '1.4rem 1.3rem 2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontFamily: SERIF, fontSize: '1.25rem', color: T.g900 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.3rem', color: T.mute, cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function EditProfileModal({ onClose, onSaved }) {
  const { user, fetchWithAuth } = useAuth()
  const [name, setName] = React.useState(user?.name || '')
  const [busy, setBusy] = React.useState(false)
  const _inp = { width: '100%', padding: '.75rem .9rem', border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: '.9rem', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', color: T.text }
  const _lbl = { display: 'block', fontSize: '.68rem', fontWeight: 700, color: T.mute, textTransform: 'uppercase', letterSpacing: '.05em', margin: '0 0 .35rem' }
  const save = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      const r = await fetchWithAuth(`${API_BASE}/api/profile`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: name.trim() }) })
      if (r.ok) { onSaved(name.trim()); onClose() }
    } catch {}
    setBusy(false)
  }
  return (
    <ModalShell title="Edit profile" onClose={onClose}>
      <label style={_lbl}>Name</label>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={_inp} />
      <label style={{ ..._lbl, marginTop: 14 }}>Phone / login</label>
      <input value={user?.email || ''} disabled style={{ ..._inp, background: T.g50, color: T.mute }} />
      <div style={{ fontSize: '.68rem', color: T.mute, marginTop: 5 }}>Your login number can't be changed here — contact support if needed.</div>
      <button onClick={save} disabled={busy || !name.trim()} style={{ width: '100%', marginTop: 18, background: (busy || !name.trim()) ? '#9bbda6' : T.g700, color: '#fff', border: 'none', borderRadius: 12, padding: '.9rem', fontWeight: 800, fontSize: '.9rem', cursor: (busy || !name.trim()) ? 'default' : 'pointer', fontFamily: FONT }}>{busy ? 'Saving…' : 'Save'}</button>
    </ModalShell>
  )
}

function NotifModal({ onClose }) {
  const { user, fetchWithAuth } = useAuth()
  const [wa, setWa] = React.useState(false)
  const [em, setEm] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [loaded, setLoaded] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const idIsEmail = user?.email && String(user.email).includes('@')

  React.useEffect(() => {
    fetchWithAuth(`${API_BASE}/api/reminders/whatsapp-pref`).then(r => r.ok ? r.json() : {})
      .then(j => { setWa(!!j.enabled); setEm(!!j.emailEnabled); setEmail(j.contactEmail || (idIsEmail ? user.email : '')); setLoaded(true) }).catch(() => setLoaded(true))
  }, [fetchWithAuth]) // eslint-disable-line

  const persist = async (next) => {
    setBusy(true)
    try { await fetchWithAuth(`${API_BASE}/api/reminders/whatsapp-pref`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: next.wa, phone: user?.email || '', emailEnabled: next.em, contactEmail: next.email }) }) } catch {}
    setBusy(false)
  }
  const Toggle = ({ on, onClick }) => (
    <button onClick={onClick} disabled={!loaded || busy} style={{ width: 46, height: 26, borderRadius: 999, border: 'none', background: on ? T.g600 : '#CBD5D0', position: 'relative', cursor: (!loaded || busy) ? 'default' : 'pointer', transition: 'background .2s', flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
    </button>
  )

  return (
    <ModalShell title="Notifications" onClose={onClose}>
      {/* Medicine reminders (delivered in-app) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.6rem 0' }}>
        <span style={{ fontSize: '1.4rem' }}>🔔</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '.9rem', color: T.text }}>Medicine reminders</div>
          <div style={{ fontSize: '.7rem', color: T.mute, marginTop: 1 }}>Dose &amp; follow-up reminders in the Revive app</div>
        </div>
        <Toggle on={wa} onClick={() => { const v = !wa; setWa(v); persist({ wa: v, em, email }) }} />
      </div>
      {/* Email */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.6rem 0', borderTop: `1px solid ${T.border}` }}>
        <span style={{ fontSize: '1.4rem' }}>✉️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '.9rem', color: T.text }}>Email updates</div>
          <div style={{ fontSize: '.7rem', color: T.mute, marginTop: 1 }}>Order & consult updates by email</div>
        </div>
        <Toggle on={em} onClick={() => { const v = !em; setEm(v); persist({ wa, em: v, email }) }} />
      </div>
      {em && (
        <input value={email} onChange={e => setEmail(e.target.value)} onBlur={() => persist({ wa, em, email })} placeholder="your@email.com" type="email"
          style={{ width: '100%', marginTop: 6, padding: '.7rem .9rem', border: `1.5px solid ${T.border}`, borderRadius: 10, fontSize: '.88rem', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', color: T.text }} />
      )}
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 12, display: 'flex', gap: 8, fontSize: '.72rem', color: T.mute, lineHeight: 1.5 }}>
        <span>🔔</span><span>Care reminders, order updates and doctor reviews are always shown inside the app too.</span>
      </div>
    </ModalShell>
  )
}

export function MobileProfile() {
  const navigate = useNavigate()
  const { user, logout, updateUser } = useAuth()
  const loggedIn = !!user?.token
  const [editOpen, setEditOpen] = React.useState(false)
  const [notifOpen, setNotifOpen] = React.useState(false)
  const name = user?.name || 'Welcome'
  const initial = ((user?.name || user?.email || '?')[0] || '?').toUpperCase()
  const contact = user?.email || 'Sign in to see your care plan'

  const Item = ({ icon, label, sub, onClick, danger, last }) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: '#fff', border: 'none', borderBottom: last ? 'none' : `1px solid ${T.border}`, padding: '.95rem 1.1rem', cursor: 'pointer', fontFamily: FONT, textAlign: 'left' }}>
      <span style={{ width: 34, height: 34, borderRadius: 10, background: danger ? '#FDECEA' : T.g50, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '.9rem', fontWeight: 600, color: danger ? '#C0392B' : T.text }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: '.68rem', color: T.mute, marginTop: 1 }}>{sub}</span>}
      </span>
      <span style={{ color: T.mute }}>›</span>
    </button>
  )
  const Section = ({ title, children }) => (
    <>
      <div style={{ fontSize: '.68rem', fontWeight: 700, color: T.mute, textTransform: 'uppercase', letterSpacing: '.06em', margin: '1.2rem .3rem .5rem' }}>{title}</div>
      <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>{children}</div>
    </>
  )

  const deleteData = () => {
    const idn = user?.email || ''
    const subj = encodeURIComponent('Data deletion / consent withdrawal request')
    const body = encodeURIComponent(`I request to withdraw my consent and permanently delete my Revive account and personal data.\n\nRegistered ID: ${idn}\n\nThank you.`)
    try { window.location.href = `mailto:support@revivehc.com?subject=${subj}&body=${body}` } catch (e) {}
  }

  return (
    <div style={{ minHeight: '100vh', background: T.g50, fontFamily: FONT, paddingBottom: 92 }}>
      {/* Header */}
      <div style={{ background: T.headerGrad, padding: '3.4rem 1.3rem 1.7rem', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,.2)', border: '2px solid rgba(255,255,255,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 800, color: '#fff', flexShrink: 0 }}>{initial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontFamily: SERIF, fontSize: '1.35rem', color: '#fff' }}>{name}</div>
            {loggedIn && <button onClick={() => setEditOpen(true)} aria-label="Edit profile" style={{ background: 'rgba(255,255,255,.22)', border: 'none', borderRadius: 8, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '.78rem', flexShrink: 0 }}>✏️</button>}
          </div>
          <div style={{ fontSize: '.76rem', color: 'rgba(255,255,255,.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact}</div>
        </div>
      </div>

      <div style={{ padding: '0 1.1rem' }}>
        {loggedIn ? (
          <>
            {/* Quick tiles (overlap the header) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: '-1rem' }}>
              {[['🩺', 'My Consults', 'Your consultations', '/m/consults'], ['📦', 'My Orders', 'Track your orders', '/m/orders']].map(([ic, l, s, to]) => (
                <div key={l} onClick={() => navigate(to)} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '1rem', cursor: 'pointer', boxShadow: '0 3px 12px rgba(13,34,24,.06)' }}>
                  <div style={{ fontSize: '1.3rem' }}>{ic}</div>
                  <div style={{ fontWeight: 700, color: T.text, fontSize: '.9rem', marginTop: 6 }}>{l}</div>
                  <div style={{ fontSize: '.66rem', color: T.mute, marginTop: 1 }}>{s}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 14 }}><ReferAndEarn /></div>

            <Section title="Settings">
              <Item icon="🔔" label="Notifications" onClick={() => setNotifOpen(true)} last />
            </Section>

            <Section title="Support">
              <Item icon="🎧" label="Help Center" onClick={() => navigate('/contact')} last />
            </Section>

            <Section title="Privacy & Legal">
              <Item icon="🔒" label="Privacy Policy" onClick={() => navigate('/privacy-policy')} />
              <Item icon="📄" label="Terms of Service" onClick={() => navigate('/terms-of-service')} />
              <Item icon="🗑️" label="Delete My Data" danger onClick={deleteData} last />
            </Section>

            <button onClick={() => { try { sessionStorage.removeItem('skip_login') } catch (e) {}; logout && logout(); navigate('/') }} style={{ width: '100%', marginTop: '1.2rem', background: '#fff', color: '#C0392B', border: `1px solid ${T.border}`, borderRadius: 14, padding: '.95rem', fontWeight: 700, fontSize: '.9rem', cursor: 'pointer', fontFamily: FONT }}>Log Out</button>

            <div style={{ textAlign: 'center', fontSize: '.66rem', color: T.mute, margin: '1.2rem 0 .4rem' }}>Revive · Doctor-led skin &amp; hair care</div>
          </>
        ) : (
          <>
            <div style={{ marginTop: '-1rem', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '1.3rem', textAlign: 'center', boxShadow: '0 3px 12px rgba(13,34,24,.06)' }}>
              <div style={{ fontSize: '.85rem', color: T.text, marginBottom: 12, lineHeight: 1.5 }}>Log in to see your care plan, orders and consults.</div>
              <button onClick={() => { try { window.dispatchEvent(new Event('show-login')) } catch (e) {} }} style={{ width: '100%', background: T.g700, color: '#fff', border: 'none', borderRadius: 12, padding: '.95rem', fontWeight: 800, fontSize: '.95rem', cursor: 'pointer', fontFamily: FONT }}>Log in / Sign up</button>
            </div>
            <Section title="Explore">
              <Item icon="🔬" label="AI Skin Analysis" onClick={() => navigate('/skin-quiz')} />
              <Item icon="🎧" label="Help Center" onClick={() => navigate('/contact')} last />
            </Section>
            <Section title="Privacy & Legal">
              <Item icon="🔒" label="Privacy Policy" onClick={() => navigate('/privacy-policy')} />
              <Item icon="📄" label="Terms of Service" onClick={() => navigate('/terms-of-service')} last />
            </Section>
          </>
        )}
      </div>

      {editOpen && <EditProfileModal onClose={() => setEditOpen(false)} onSaved={(name) => updateUser && updateUser({ name })} />}
      {notifOpen && <NotifModal onClose={() => setNotifOpen(false)} />}
    </div>
  )
}

// ───────────────────────── MY ORDERS ─────────────────────────

const ORDER_STATUS = {
  placed:     ['Placed', T.g700, T.g100],
  verifying:  ['Verifying Rx', '#B7770D', '#FFF8E1'],
  dispatched: ['Dispatched', T.g700, T.g100],
  delivered:  ['Delivered', T.g700, T.g100],
  cancelled:  ['Cancelled', '#C0392B', '#FFF0F0'],
}
const RX_STATUS_NOTE = {
  pending_verification: ['🕑', '#B7770D', 'Prescription under pharmacist review'],
  verified:             ['✓', T.g600, 'Prescription verified by our pharmacist'],
  rejected:             ['⚠', '#C0392B', 'Prescription could not be verified — please contact us'],
}

export function MobileOrders() {
  const navigate = useNavigate()
  const { user, fetchWithAuth } = useAuth()
  const [orders, setOrders] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const parse = (str, d = []) => { try { return JSON.parse(str || '') ?? d } catch { return d } }

  React.useEffect(() => {
    if (!user?.token) { setLoading(false); return }
    let alive = true
    fetchWithAuth(`${API_BASE}/api/orders`).then(r => r.ok ? r.json() : { orders: [] })
      .then(j => { if (alive) { setOrders(j.orders || []); setLoading(false) } })
      .catch(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [user, fetchWithAuth])

  return (
    <div style={{ minHeight: '100vh', background: T.g50, fontFamily: FONT, paddingBottom: 92 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2.8rem 1.1rem 1rem', background: '#fff', borderBottom: `1px solid ${T.border}` }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: T.g900, cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
        <div style={{ fontFamily: SERIF, fontSize: '1.35rem', color: T.g900 }}>My Orders</div>
      </div>

      <div style={{ padding: '1.1rem' }}>
        {loading && <div style={{ textAlign: 'center', color: T.mute, padding: '2rem' }}>Loading…</div>}
        {!loading && orders.length === 0 && (
          <div style={{ textAlign: 'center', color: T.mute, padding: '3rem 1rem' }}>
            <div style={{ fontSize: '2.4rem', opacity: .2, marginBottom: 10 }}>📦</div>
            <div style={{ fontWeight: 600, color: T.text }}>No orders yet</div>
            <button onClick={() => navigate('/')} style={{ marginTop: 16, background: T.g700, color: '#fff', border: 'none', borderRadius: 999, padding: '.7rem 1.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Browse products</button>
          </div>
        )}
        {orders.map(o => {
          const items = parse(o.items), addr = parse(o.address, {})
          const [sLabel, sColor, sBg] = ORDER_STATUS[o.status] || ORDER_STATUS.placed
          const rxNote = o.rxRequired ? RX_STATUS_NOTE[o.rxStatus] : null
          return (
            <div key={o.id} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '1rem 1.1rem', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '.72rem', color: T.mute }}>{(o.createdAt || '').slice(0, 10)}</div>
                <span style={{ fontSize: '.66rem', fontWeight: 800, color: sColor, background: sBg, borderRadius: 999, padding: '.2rem .7rem' }}>{sLabel}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto' }}>
                {items.map((it, i) => (
                  <div key={i} style={{ width: 48, height: 48, borderRadius: 10, background: '#fff', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                    {it.imgs?.[0] ? <img src={cld(it.imgs[0], 100)} alt="" style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} /> : <span style={{ fontSize: '.9rem', opacity: .5 }}>🧴</span>}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '.8rem', color: T.text, marginTop: 8, lineHeight: 1.4 }}>{items.map(i => `${i.name} ×${i.quantity || 1}`).join(', ')}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <div style={{ fontSize: '.72rem', color: T.mute }}>{[addr.area, addr.city, addr.pincode].filter(Boolean).join(', ')}</div>
                <div style={{ fontWeight: 800, color: T.g900 }}>₹{o.amount}</div>
              </div>
              {rxNote && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: '.74rem', fontWeight: 600, color: rxNote[1] }}>
                  <span>{rxNote[0]}</span><span>{rxNote[2]}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ───────────────────────── TAB BAR ─────────────────────────

export function MobileTabBar() {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { user, fetchWithAuth } = useAuth()
  const loggedIn = !!user?.token

  // "My Care" is treatment tracking — only meaningful once the patient has
  // actually consulted. Hide the tab for brand-new users (no consultations yet),
  // so they see the "book a consultation" journey first. Cache the answer in
  // localStorage so returning patients don't see the tab flicker in.
  const [hasConsulted, setHasConsulted] = React.useState(() => {
    try { return localStorage.getItem('revive_has_consulted') === '1' } catch { return false }
  })
  React.useEffect(() => {
    if (!loggedIn || !fetchWithAuth || !user?.email) return
    let alive = true
    fetchWithAuth(`${API_BASE}/api/followups?email=${encodeURIComponent(user.email)}`)
      .then(r => r.ok ? r.json() : { followups: [] })
      .then(j => {
        if (!alive) return
        const has = (j.followups || []).length > 0
        setHasConsulted(has)
        try { localStorage.setItem('revive_has_consulted', has ? '1' : '0') } catch {}
      })
      .catch(() => {})
    return () => { alive = false }
  }, [loggedIn, user, fetchWithAuth])

  const tabs = loggedIn
    ? [
        { ic: '🌱', lb: 'Journey', to: '/patient-dashboard', active: pathname === '/patient-dashboard' && !search.includes('view=care') },
        ...(hasConsulted ? [{ ic: '📋', lb: 'My Care', to: '/patient-dashboard?view=care', active: pathname === '/patient-dashboard' && search.includes('view=care') }] : []),
        { ic: '🩺', lb: 'Consult', to: '/m/consult', active: pathname === '/m/consult' },
        { ic: '🛍️', lb: 'Shop',    to: '/', active: pathname === '/' },
        { ic: '👤', lb: 'Profile', to: '/m/profile', active: pathname === '/m/profile' },
      ]
    : [
        { ic: '🏠', lb: 'Home',    to: '/', active: pathname === '/' },
        { ic: '🩺', lb: 'Consult', to: '/m/consult', active: pathname === '/m/consult' },
        { ic: '🛍️', lb: 'Shop',    to: '/m/categories', active: pathname === '/m/categories' },
        { ic: '👤', lb: 'Profile', to: '/m/profile', active: pathname === '/m/profile' },
      ]

  if (pathname.startsWith('/admin') || pathname.startsWith('/doctor') || pathname.startsWith('/product') || pathname.startsWith('/checkout')) return null
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 900, background: '#fff', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-around', padding: '10px 0 20px' }}>
      {tabs.map(({ ic, lb, to, active }) => (
        <button key={lb} onClick={() => navigate(to, { state: { _t: Date.now() } })} style={{ background: 'none', border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', color: active ? T.g700 : T.mute, fontWeight: active ? 800 : 500, fontFamily: FONT }}>
          <span style={{ fontSize: '1.2rem', opacity: active ? 1 : .55 }}>{ic}</span>
          <span style={{ fontSize: '.6rem' }}>{lb}</span>
        </button>
      ))}
    </div>
  )
}

// ───────────────────────── PRODUCT DETAIL (native, honest) ─────────────────────────

export function MobileProductDetail({ onAdd, cartCount, onCartOpen }) {
  const navigate = useNavigate()
  const { products } = useProducts()
  const { id } = useParams()
  const product = products.find(p => String(p.id) === String(id))

  const [active, setActive] = React.useState(0)
  const [qty, setQty] = React.useState(1)
  const [expanded, setExpanded] = React.useState(false)
  const [reviews, setReviews] = React.useState(null) // null = loading
  const [added, setAdded] = React.useState(false)
  const [lightbox, setLightbox] = React.useState(false)
  const [zoom, setZoom] = React.useState(1)

  React.useEffect(() => { window.scrollTo(0, 0); setActive(0); setQty(1) }, [id])
  React.useEffect(() => {
    setReviews(null)
    fetch(`${API_BASE}/api/product/${id}/reviews`).then(r => r.ok ? r.json() : []).then(d => setReviews(Array.isArray(d) ? d : [])).catch(() => setReviews([]))
  }, [id])

  if (!product) return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, paddingBottom: 92 }}>
      <div style={{ fontSize: '2.5rem', opacity: .2 }}>🔍</div>
      <div style={{ fontWeight: 700, color: T.g900 }}>Product not found</div>
      <button onClick={() => navigate('/shop')} style={{ background: T.g700, color: '#fff', border: 'none', borderRadius: 999, padding: '.7rem 1.6rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT }}>Back to Shop</button>
    </div>
  )

  const imgs = (product.imgs || []).filter(Boolean)
  const tags = (product.tags || []).filter(Boolean).slice(0, 6)
  // Honest pricing — only show a discount when a real MRP is present in the data
  const mrp = (product.mrp && product.mrp > product.price) ? product.mrp : null
  const off = mrp ? Math.round(((mrp - product.price) / mrp) * 100) : null
  // Honest ratings — only from real submitted reviews; never invented
  const rated = (reviews || []).filter(r => r && r.rating)
  const avg = rated.length ? (rated.reduce((s, r) => s + Number(r.rating || 0), 0) / rated.length) : null
  const related = products.filter(p => p.brand === product.brand && p.id !== product.id && p.imgs && p.imgs[0]).slice(0, 8)

  const handleAdd = () => { for (let k = 0; k < qty; k++) onAdd(product); setAdded(true); setTimeout(() => setAdded(false), 1600) }
  const handleBuy = () => { for (let k = 0; k < qty; k++) onAdd(product); navigate('/checkout') }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: FONT, paddingBottom: 96 }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 10, padding: '2.6rem 1.1rem .7rem', background: '#fff', borderBottom: `1px solid ${T.border}` }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: T.g900, cursor: 'pointer', padding: 0, lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, fontSize: '.72rem', color: T.mute, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.brand ? `${product.brand} · ` : ''}{product.cat}</div>
        <button onClick={onCartOpen ? onCartOpen : () => navigate('/view-cart')} style={{ position: 'relative', background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer' }}>
          🛒{cartCount > 0 && <span style={{ position: 'absolute', top: -6, right: -8, background: T.g600, color: '#fff', borderRadius: 999, fontSize: '.58rem', fontWeight: 700, padding: '1px 5px' }}>{cartCount}</span>}
        </button>
      </div>

      {/* Gallery */}
      <div style={{ padding: '1rem 1.1rem 0' }}>
        <div onClick={() => imgs[active] && (setZoom(1), setLightbox(true))} style={{ position: 'relative', width: '100%', height: 300, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'zoom-in' }}>
          {imgs[active] ? <img src={cld(imgs[active], 600)} alt={product.name} style={{ maxWidth: '82%', maxHeight: '82%', objectFit: 'contain' }} /> : <span style={{ fontSize: '3rem', opacity: .2 }}>🧴</span>}
          {imgs[active] && <span style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: '.62rem', fontWeight: 600, padding: '.25rem .6rem', borderRadius: 999 }}>🔍 Tap to zoom</span>}
        </div>
        {imgs.length > 1 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {imgs.map((src, i) => (
              <div key={i} onClick={() => setActive(i)} style={{ width: 58, height: 58, flexShrink: 0, borderRadius: 10, background: '#fff', border: `2px solid ${i === active ? T.g600 : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer' }}>
                <img src={cld(src, 120)} alt="" style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '1.2rem 1.1rem 0' }}>
        {product.brand && <div style={{ fontSize: '.72rem', color: T.g700, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>{product.brand}</div>}
        <div style={{ fontFamily: SERIF, fontSize: '1.5rem', color: T.text, lineHeight: 1.2, marginTop: 3 }}>{product.name}</div>

        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {tags.map(t => <span key={t} style={{ fontSize: '.68rem', color: T.g700, background: T.g100, border: `1px solid ${T.border}`, borderRadius: 999, padding: '.25rem .7rem', fontWeight: 600 }}>{t}</span>)}
          </div>
        )}

        {/* Rating — only if real reviews exist */}
        {reviews !== null && (rated.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
            <span style={{ color: '#F5A623', fontSize: '.95rem' }}>{'★'.repeat(Math.round(avg))}{'☆'.repeat(5 - Math.round(avg))}</span>
            <span style={{ fontSize: '.78rem', color: T.text, fontWeight: 700 }}>{avg.toFixed(1)}</span>
            <span style={{ fontSize: '.74rem', color: T.mute }}>({rated.length} review{rated.length === 1 ? '' : 's'})</span>
          </div>
        ) : (
          <div style={{ fontSize: '.74rem', color: T.mute, marginTop: 12 }}>No reviews yet</div>
        ))}

        {/* Price */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 14 }}>
          <span style={{ fontFamily: SERIF, fontSize: '1.9rem', color: T.g900 }}>₹{product.price}</span>
          {mrp && <span style={{ fontSize: '.95rem', color: T.mute, textDecoration: 'line-through' }}>₹{mrp}</span>}
          {off && <span style={{ fontSize: '.72rem', fontWeight: 800, color: '#fff', background: T.g600, borderRadius: 6, padding: '.15rem .5rem' }}>{off}% OFF</span>}
        </div>
        <div style={{ fontSize: '.68rem', color: T.mute, marginTop: 2 }}>Inclusive of all taxes</div>

        {/* Prescription-required notice */}
        {isRx(product) && (
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 9, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 12, padding: '.8rem .9rem' }}>
            <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>℞</span>
            <div style={{ fontSize: '.74rem', color: '#7a5b12', lineHeight: 1.5 }}><b>Prescription required.</b> This is a prescription medicine. You'll need a valid prescription from a registered doctor — our licensed pharmacy verifies it before dispatch.</div>
          </div>
        )}

        {/* Quantity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
          <span style={{ fontSize: '.85rem', fontWeight: 700, color: T.text }}>Quantity</span>
          <div style={{ display: 'flex', alignItems: 'center', border: `1.5px solid ${T.border}`, borderRadius: 999, overflow: 'hidden' }}>
            <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 40, height: 40, border: 'none', background: '#fff', color: T.g700, fontSize: '1.3rem', cursor: 'pointer', fontFamily: FONT }}>−</button>
            <span style={{ minWidth: 30, textAlign: 'center', fontWeight: 800, color: T.g900 }}>{qty}</span>
            <button onClick={() => setQty(q => q + 1)} style={{ width: 40, height: 40, border: 'none', background: '#fff', color: T.g700, fontSize: '1.2rem', cursor: 'pointer', fontFamily: FONT }}>+</button>
          </div>
        </div>

        {/* Honest trust row */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {[['✅', 'Genuine'], ['🔒', 'Secure checkout'], ['🩺', 'Derma store']].map(([ic, lb]) => (
            <div key={lb} style={{ flex: 1, background: T.g50, border: `1px solid ${T.border}`, borderRadius: 12, padding: '.6rem .3rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1rem' }}>{ic}</div>
              <div style={{ fontSize: '.6rem', color: T.text, fontWeight: 600, marginTop: 3, lineHeight: 1.2 }}>{lb}</div>
            </div>
          ))}
        </div>

        {/* Description */}
        {product.desc && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: '1rem', fontWeight: 800, color: T.text, marginBottom: 6 }}>About this product</div>
            <div style={{ fontSize: '.85rem', color: '#4a5a52', lineHeight: 1.6, maxHeight: expanded ? 'none' : 88, overflow: 'hidden' }}>{product.desc}</div>
            {product.desc.length > 160 && (
              <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: T.g700, fontWeight: 700, fontSize: '.8rem', cursor: 'pointer', padding: '6px 0', fontFamily: FONT }}>{expanded ? 'Read less ▲' : 'Read more ▼'}</button>
            )}
          </div>
        )}

        {/* Consult cross-sell */}
        <div onClick={() => navigate('/m/consult')} style={{ marginTop: 18, background: '#FDF3EE', border: '1px solid #F1D9CC', borderRadius: 16, padding: '1rem 1.1rem', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <span style={{ fontSize: '1.6rem' }}>👩‍⚕️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, color: T.g900, fontSize: '.88rem' }}>Not sure if this suits your skin?</div>
            <div style={{ fontSize: '.72rem', color: '#8a6a58', marginTop: 2 }}>Consult a dermatologist before you buy →</div>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: T.text, padding: '1.6rem 1.1rem .8rem' }}>More from {product.brand}</div>
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '0 1.1rem .4rem', WebkitOverflowScrolling: 'touch' }}>
            {related.map(p => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}
          </div>
        </>
      )}

      {/* Fullscreen zoom lightbox */}
      {lightbox && imgs[active] && (
        <div onClick={() => { setLightbox(false); setZoom(1) }} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,.94)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(false); setZoom(1) }} style={{ position: 'absolute', top: '2.6rem', right: '1.1rem', width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.25)', color: '#fff', fontSize: '1.3rem', cursor: 'pointer', zIndex: 2 }}>✕</button>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', height: '84vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ZoomableImage key={active} src={cld(imgs[active], 1200)} alt={product.name}
              onNext={() => setActive(i => Math.min(i + 1, imgs.length - 1))}
              onPrev={() => setActive(i => Math.max(i - 1, 0))} />
          </div>
          {imgs.length > 1 && (
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: '1.6rem', left: 0, right: 0, display: 'flex', gap: 8, justifyContent: 'center' }}>
              {imgs.map((src, i) => (
                <div key={i} onClick={() => { setActive(i); setZoom(1) }} style={{ width: 46, height: 46, borderRadius: 8, background: '#fff', border: `2px solid ${i === active ? '#fff' : 'rgba(255,255,255,.35)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <img src={cld(src, 100)} alt="" style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
                </div>
              ))}
            </div>
          )}
          <div style={{ position: 'absolute', top: '2.8rem', left: '1.2rem', color: 'rgba(255,255,255,.7)', fontSize: '.72rem' }}>Swipe to change photo · pinch or double-tap to zoom</div>
        </div>
      )}

      {/* Sticky action bar */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30, background: '#fff', borderTop: `1px solid ${T.border}`, padding: '.7rem 1.1rem 1.3rem', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={handleAdd} style={{ flex: 1, background: added ? T.g100 : '#fff', color: T.g700, border: `1.5px solid ${T.g600}`, borderRadius: 14, padding: '.9rem .5rem', fontWeight: 800, fontSize: '.88rem', cursor: 'pointer', fontFamily: FONT }}>
          {added ? '✓ Added' : '🛒 Add to Cart'}
        </button>
        <button onClick={handleBuy} style={{ flex: 1.25, background: T.g700, color: '#fff', border: 'none', borderRadius: 14, padding: '.9rem .5rem', fontWeight: 800, fontSize: '.88rem', cursor: 'pointer', fontFamily: FONT }}>
          Buy Now · ₹{product.price * qty}
        </button>
      </div>
    </div>
  )
}
